/**
 * PATH: packages/ui/src/kanban/meetingSessionPersistence.ts
 * FILE_ID: UI-KANBAN-MEETING-SESSION-PERSIST-001
 * PURPOSE: sessionStorage handoff for the live-meeting singleton — lets a
 *   same-tab refresh resume a live recording — plus the beforeunload guard
 *   that warns the operator before they refresh/close mid-recording.
 * WHY: BUG-2026-07-02-MEETING-RECORDING-DIES-ON-NAV (ARC-7 MR1) — refresh
 *   used to lose the meeting entirely. sessionStorage (NOT local) is
 *   deliberate: refresh in the SAME tab resumes; a second tab must not
 *   silently grab the mic for a meeting it can't see.
 * FLOW: persist on start/resume → readPersisted on next start() → clear on
 *   stop/end/permanent-failure.
 * RELATED:
 *   - meetingSession.ts: sole consumer.
 */

export interface PersistedMeeting {
  meetingId: string
  boardId: string
  language?: string
  ts: number
}

const PERSIST_KEY = 'kanban:live-meeting:v1'
/** A persisted handoff older than this is stale — don't auto-resume. */
const PERSIST_MAX_AGE_MS = 4 * 60 * 60 * 1000

export function readPersistedMeeting(): PersistedMeeting | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(PERSIST_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as PersistedMeeting
    if (!p || typeof p.meetingId !== 'string' || typeof p.boardId !== 'string') {
      return null
    }
    if (typeof p.ts !== 'number' || Date.now() - p.ts > PERSIST_MAX_AGE_MS) {
      return null
    }
    return p
  } catch {
    return null
  }
}

export function persistMeeting(p: Omit<PersistedMeeting, 'ts'>): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      PERSIST_KEY,
      JSON.stringify({ ...p, ts: Date.now() } satisfies PersistedMeeting),
    )
  } catch { /* ignore quota / private mode */ }
}

export function clearPersistedMeeting(): void {
  if (typeof window === 'undefined') return
  try { window.sessionStorage.removeItem(PERSIST_KEY) } catch { /* ignore */ }
}

/**
 * Warn before refresh/close while recording. Returns an unbind function.
 * If the operator proceeds anyway, the persisted handoff lets the next
 * page load resume the meeting.
 */
export function bindBeforeUnloadGuard(isRecording: () => boolean): () => void {
  if (typeof window === 'undefined') return () => undefined
  const handler = (e: BeforeUnloadEvent) => {
    if (!isRecording()) return
    e.preventDefault()
    // Chrome requires returnValue to be set for the native prompt.
    e.returnValue = ''
  }
  window.addEventListener('beforeunload', handler)
  return () => window.removeEventListener('beforeunload', handler)
}
